import React from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveContainer, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';
import ChartLegend from './ChartLegend';
import { fontSizes } from '../../theme/theme';

interface BarChartData {
  [key: string]: string | number;
}

interface BarChartProps extends ChartHeaderProps {
  data: BarChartData[];
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  stacked?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
  onDetailsClick?: (data?: any) => void;
  gridExist?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 300,
  graphHeight = 300,
  stacked = false,
  xAxisTitle,
  yAxisTitle,
  details,
  onDetailsClick,
  gridExist = false
}) => {

  const handleBarClick = (data: any) => {
    console.log('Bar clicked:', data);
    if (onDetailsClick) {
      onDetailsClick(data);
    }
  };

  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      {details?.label && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
          {details.label || ''}
        </Typography>
      )}
      {details?.subLabel && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
          {details.subLabel || ''}
        </Typography>
      )}
      <ResponsiveContainer width="100%" minHeight={graphHeight}>
        <RechartsBarChart
          data={data}
          margin={{ top: 20, right: 10, left: 0, bottom: 15 }}
          barCategoryGap="35%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: fontSizes.graph.axis, fill: '#6B7280' }}
            label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 15, style: { textAnchor: 'middle', fontSize: fontSizes.graph.axis, fill: '#374151' } } : undefined}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              fontSize: `${fontSizes.graph.tooltip}px`
            }}
            cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
          />
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[index % colors.length]}
              stackId={stacked ? "a" : undefined}
              radius={stacked ? 0 : [4, 4, 0, 0]}
              maxBarSize={35}
              onClick={handleBarClick}
              style={{ cursor: gridExist ? 'pointer' : 'default' }}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
      <ChartLegend 
        bottomDescription={details?.bottomDescription}
        subPoints={details?.subPoints}
        colors={colors}
      />
    </ChartCardWrapper>
  );
};

export default BarChart;
